import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { GraduationCap, MapPin, Mail, ArrowLeft, Calendar } from 'lucide-react';
import { InstructorAvatar } from '@/components/InstructorAvatar';
import SEO from '@/components/SEO';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';
import { SummerFoundingPartnerBadge } from '@/components/SummerFoundingPartnerBadge';
import { LocationAlertSignup } from '@/components/LocationAlertSignup';
import { resolveMetro } from '@/lib/usMetros';
import { US_STATES } from '@/lib/usStates';

const STATE_SLUG_BY_CODE = Object.fromEntries(US_STATES.map(([code, _name, slug]) => [code, slug]));
const STATE_NAME_BY_CODE = Object.fromEntries(US_STATES.map(([code, name, _slug]) => [code, name]));

/**
 * City-scoped instructor hub — `/instructors/atlanta-ga`,
 * `/instructors/austin-tx`, etc. Targets high-intent searches like
 * "Mahjong class Atlanta" or "American Mahjong instructor Austin".
 */
export default function InstructorsByCityPage() {
  const params = useParams();
  const slug = params.slug || params.state || params.eventId || '';
  const metro = resolveMetro(slug);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!metro) { setLoading(false); return; }
    setLoading(true);
    api.get(`/instructors/by-city/${metro.slug}`)
      .then(r => setInstructors(r.data.instructors || []))
      .catch(() => setInstructors([]))
      .finally(() => setLoading(false));
  }, [metro]);

  if (!metro) return <Navigate to="/instructors" replace />;

  const locLabel = `${metro.name}, ${metro.state}`;
  const stateSlug = STATE_SLUG_BY_CODE[metro.state];
  const stateName = STATE_NAME_BY_CODE[metro.state] || metro.state;
  const title = `American Mahjong Instructors in ${locLabel}`;
  const description = `Find American Mahjong instructors in ${locLabel} offering group classes, private lessons, and Mahjong events. ${instructors.length} ${instructors.length === 1 ? 'instructor' : 'instructors'} currently listed on Meet For Mahjong.`;

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    numberOfItems: instructors.length,
    itemListElement: instructors.map((inst, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://meetformahjong.com/player/${inst.id}`,
      name: inst.display_name,
    })),
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="instructors-by-city-page">
      <SEO
        title={title}
        description={description}
        path={`/instructors/${metro.slug}`}
        jsonLd={itemListJsonLd}
      />

      <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
        <Link to="/instructors" className="inline-flex items-center gap-1 text-slate-500 hover:text-[#b456df]">
          <ArrowLeft className="w-4 h-4" /> All instructors
        </Link>
        {stateSlug && (
          <>
            <span className="text-slate-300">·</span>
            <Link to={`/instructors/${stateSlug}`} className="text-slate-500 hover:text-[#b456df]">
              All {stateName} instructors
            </Link>
          </>
        )}
      </div>

      <div className="text-center mb-10">
        <div className="inline-block px-3 py-1 rounded-full bg-[#b456df]/10 text-[#b456df] text-xs font-semibold tracking-wider uppercase mb-3">
          {locLabel}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {title}
        </h1>
        <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
          {instructors.length > 0
            ? `Connect directly with ${instructors.length} American Mahjong ${instructors.length === 1 ? 'instructor' : 'instructors'} in ${locLabel}. Lessons, classes, and local events for beginners through advanced players.`
            : `No instructors are listed in ${locLabel} yet — Meet For Mahjong is growing fast. If you teach in ${metro.name}, sign up free to reach players in your area. Or check nearby cities in ${stateName}.`}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" />
        </div>
      ) : instructors.length === 0 ? (
        <div className="text-center py-12" data-testid="city-hub-instructors-empty-state">
          <GraduationCap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to={`/events/${metro.slug}`}>
              <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]">
                Mahjong events in {metro.name}
              </Button>
            </Link>
            {stateSlug && (
              <Link to={`/instructors/${stateSlug}`}>
                <Button variant="outline" className="rounded-full">All {stateName} instructors</Button>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="city-hub-instructors-grid">
          {instructors.map(inst => (
            <Card
              key={inst.id}
              className={`border-slate-100 hover:shadow-md transition-all ${inst.is_premium_instructor ? 'ring-1 ring-amber-300/60' : ''}`}
              data-testid={`city-hub-instructor-${inst.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <InstructorAvatar
                    photoUrl={inst.profile_photo}
                    name={inst.display_name}
                    testId={`city-hub-instructor-avatar-${inst.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/player/${inst.id}`}
                        className="font-semibold text-lg hover:text-[#b456df]"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {inst.display_name}
                      </Link>
                      {inst.is_premium_instructor && <PremiumInstructorBadge size="sm" />}
                      {inst.is_summer_founding_partner && <SummerFoundingPartnerBadge size="sm" />}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
                      {(inst.city || inst.zip_code) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {inst.city ? `${inst.city}, ${metro.state}` : inst.zip_code}
                        </span>
                      )}
                      <span className="capitalize">{inst.skill_level} level</span>
                    </div>
                    {inst.instructor_bio && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{inst.instructor_bio}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(inst.instructor_lesson_types || []).map(t => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-[#b456df]/10 text-[#b456df] text-xs capitalize">{t}</span>
                      ))}
                    </div>
                    {inst.instructor_rate && <p className="text-xs text-slate-400 mt-2">Rate: {inst.instructor_rate}</p>}
                    <div className="flex gap-2 mt-3">
                      <Link to={`/player/${inst.id}`}>
                        <Button size="sm" className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] text-xs">
                          View profile
                        </Button>
                      </Link>
                      {inst.email && (
                        <a href={`mailto:${inst.email}`}>
                          <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs">
                            <Mail className="w-3 h-3" /> Email
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-10 text-center">
        <Link
          to={`/events/${metro.slug}`}
          className="inline-flex items-center gap-2 text-sm text-[#56c6df] hover:underline"
          data-testid="city-hub-cross-link-events"
        >
          <Calendar className="w-4 h-4" />
          See upcoming Mahjong events in {metro.name} →
        </Link>
      </div>

      <LocationAlertSignup
        type="instructor"
        stateSlug={stateSlug}
        citySlug={metro.slug}
        locationLabel={locLabel}
      />
    </div>
  );
}
